import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PostCard from '../components/PostCard/PostCard';
import CreatePostModal from '../components/CreatePostModal/CreatePostModal';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import hiveService from '../services/hive';
import api, { userApi } from '../services/api';
import './Feed.css';

const SORT_OPTIONS = [
  { value: 'created', label: 'Recent' },
  { value: 'trending', label: 'Trending' },
  { value: 'payout', label: 'Payout' },
  { value: 'following', label: 'Following', requiresAuth: true },
];
const VALID_SORTS = new Set(SORT_OPTIONS.map(o => o.value));

const FeedView = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { community, sort: sortParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showCommunityPinned, updateShowCommunityPinned, saveSettings } = useSettings();
  const [posts, setPosts] = useState([]);
  const [communityInfo, setCommunityInfo] = useState(null);
  const [postsLoading, setPostsLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [timeWindow, setTimeWindow] = useState('recent');
  const [feedPaused, setFeedPaused] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [isSubscribeHovered, setIsSubscribeHovered] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const mobileActionsRef = React.useRef(null);

  const activeSort = VALID_SORTS.has(sortParam) ? sortParam : 'created';

  // Fetch community info from the feed endpoint on load (sets title, subscribers, etc.)
  // Subscription status is fetched separately via the DB-backed API — no stale cache.
  useEffect(() => {
    if (!community || !user?.username) return;
    userApi.checkSubscription(user.username, community)
      .then(data => setIsSubscribed(!!data.isSubscribed))
      .catch(() => setIsSubscribed(false));
  }, [community, user?.username]);

  // Close mobile actions dropdown on outside click
  useEffect(() => {
    if (!showMobileActions) return;
    const handler = (e) => {
      if (mobileActionsRef.current && !mobileActionsRef.current.contains(e.target)) {
        setShowMobileActions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMobileActions]);

  const handleSubscribe = async () => {
    if (!user) return;
    setSubscribeLoading(true);
    try {
      if (isSubscribed) {
        await hiveService.unsubscribeCommunity(user.username, community);
        setIsSubscribed(false);
        setCommunityInfo(prev => prev ? { ...prev, subscribers: Math.max(0, (prev.subscribers || 0) - 1) } : prev);
      } else {
        await hiveService.subscribeCommunity(user.username, community);
        setIsSubscribed(true);
        setCommunityInfo(prev => prev ? { ...prev, subscribers: (prev.subscribers || 0) + 1 } : prev);
      }
      // Refresh DB subscription cache in the background (fire-and-forget)
      userApi.refreshSubscriptions(user.username).catch(() => {});
    } catch (err) {
      console.error('Subscribe error:', err);
      alert(err.message || 'Failed to update subscription');
    } finally {
      setSubscribeLoading(false);
      setShowMobileActions(false);
    }
  };



  const loadCommunity = async (isLoadMore = false, overrideTimeWindow = null, overrideOffset = null) => {
    if (activeSort === 'following' && !user?.username) return;
    try {
      setPostsLoading(true);
      if (!isLoadMore) setPosts([]);
      
      const currentOffset = overrideOffset !== null ? overrideOffset : (isLoadMore ? offset : 0);
      const currentTimeWindow = overrideTimeWindow || timeWindow;

      const params = new URLSearchParams({ 
        sort: activeSort, 
        limit: 20,
        offset: currentOffset,
        timeWindow: currentTimeWindow
      });
      if (user?.username) params.set('observer', user.username);
      
      const response = await api.get(`/feed/community/${community}`, { params });
      const data = response.data;
      if (data.community) setCommunityInfo(prev => ({ ...prev, ...data.community }));
      
      if (isLoadMore) {
        setPosts(prev => [...prev, ...(data.posts || [])]);
      } else {
        setPosts(data.posts || []);
      }

      if (!data.hasMore && currentTimeWindow === 'recent' && activeSort === 'trending') {
        setHasMore(false);
        setFeedPaused(true);
      } else {
        setHasMore(data.hasMore);
        setFeedPaused(false);
      }

      setOffset(currentOffset + 20);
    } catch (err) {
      console.error('Error loading community:', err);
      setError('Failed to load community');
    } finally {
      setPostsLoading(false);
    }
  };

  useEffect(() => {
    setOffset(0);
    setTimeWindow('recent');
    setFeedPaused(false);
    loadCommunity(false, 'recent', 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [community, activeSort, user?.username]);

  const handleLoadOlderPosts = () => {
    setFeedPaused(false);
    setTimeWindow('older');
    setOffset(0);
    loadCommunity(false, 'older', 0);
  };

  const handleSortChange = (newSort) => {
    navigate(`/c/${community}/${newSort}`);
  };

  const handlePinnedToggle = () => {
    const next = !showCommunityPinned;
    updateShowCommunityPinned(next);
    if (user?.username) saveSettings({ showCommunityPinned: next });
  };

  const visiblePosts = React.useMemo(() => {
    if (showCommunityPinned || !posts.length) return posts;

    const pinned = posts.filter(p => p.stats?.is_pinned);
    const unpinned = posts.filter(p => !p.stats?.is_pinned);

    if (!pinned.length) return posts;

    // To place pinned posts in their natural order without disrupting the complex
    // Hive trending sort of the unpinned posts, we insert them based on a heuristic.
    let result = [...unpinned];
    
    for (const p of pinned) {
      let insertIndex = result.length;
      if (activeSort === 'created') {
        const pTime = new Date(p.created || 0).getTime();
        insertIndex = result.findIndex(unp => new Date(unp.created || 0).getTime() < pTime);
      } else {
        // Fallback to payout proxy for trending
        const getPayout = (post) => parseFloat((post.payout?.total || '0').toString().split(' ')[0]) + parseFloat((post.payout?.pending || '0').toString().split(' ')[0]);
        const pPayout = getPayout(p);
        insertIndex = result.findIndex(unp => getPayout(unp) < pPayout);
      }
      
      if (insertIndex === -1) insertIndex = result.length;
      result.splice(insertIndex, 0, p);
    }
    
    return result;
  }, [posts, showCommunityPinned, activeSort]);

  const renderPinnedRow = (isDesktop) => (
    <div className={`community-pinned-row ${isDesktop ? 'desktop-only' : 'mobile-only'}`}>
      {/* Desktop: Subscribe + Post buttons live here, next to pinned toggle */}
      {isDesktop && user && (
        <div className="community-desktop-actions">
          <button
            className={`community-subscribe-btn${isSubscribed ? ' subscribed' : ''}`}
            onClick={handleSubscribe}
            disabled={subscribeLoading}
            aria-label={isSubscribed ? 'Unsubscribe from community' : 'Subscribe to community'}
            onMouseEnter={() => setIsSubscribeHovered(true)}
            onMouseLeave={() => setIsSubscribeHovered(false)}
          >
            {subscribeLoading ? '…' : isSubscribed ? (isSubscribeHovered ? 'Unsubscribe' : 'Subscribed') : 'Subscribe'}
          </button>
          <Link
            to={`/create-article?community=${community}`}
            className="community-create-btn"
          >
            + Post
          </Link>
        </div>
      )}
      <span className="community-pinned-label">Highlight pinned posts</span>
      <button
        type="button"
        className={`currency-toggle ${showCommunityPinned ? 'right' : 'left'}`}
        onClick={handlePinnedToggle}
        aria-label="Toggle pinned posts"
      >
        <span className={`currency-option ${!showCommunityPinned ? 'active' : ''}`}>Off</span>
        <span className={`currency-option ${showCommunityPinned ? 'active' : ''}`}>On</span>
        <span className="currency-slider" />
      </button>
    </div>
  );

  const stickyControls = (
    <div className="community-stick">
      <div className="community-header-bar">
        <button
          className="menu-open-btn left-toggle"
          onClick={onOpenLeftSidebar}
          aria-label="Open left menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <div className="community-header-bar-info">
          <h2 className="community-header-bar-title">
            {communityInfo?.title || communityInfo?.name || `c/${community}`}
          </h2>
          <div className="community-header-bar-meta">
            <span className="community-header-bar-slug">c/{community}</span>
            {communityInfo?.subscribers != null && (
              <>
                <span className="community-header-bar-dot">&middot;</span>
                <span className="community-header-bar-members">
                  {communityInfo.subscribers.toLocaleString()} members
                </span>
                {(communityInfo?.about || communityInfo?.description) && (
                  <button
                    className="community-about-info-btn"
                    onClick={() => setShowAboutModal(true)}
                    aria-label="About this community"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </button>
                )}
              </>
            )}
            {user && isSubscribed && (
              <span className="community-header-subscribed-label mobile-only">Subscribed</span>
            )}
          </div>
        </div>
        <div className="community-header-bar-actions">
          {renderPinnedRow(true)}
          {/* Mobile only: ⋯ dropdown with subscribe + post */}
          {user && (
            <>
              <Link
                to={`/create-article?community=${community}`}
                className="community-create-btn"
              >
                + Post
              </Link>
              <div className="community-actions-menu" ref={mobileActionsRef}>
                <button
                  className="community-actions-trigger"
                  onClick={() => setShowMobileActions(v => !v)}
                  aria-label="Community actions"
                  aria-expanded={showMobileActions}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="5" cy="12" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="19" cy="12" r="2" />
                  </svg>
                </button>
                {showMobileActions && (
                  <div className="community-actions-dropdown">
                    <Link
                      to={`/create-article?community=${community}`}
                      className="community-actions-item"
                      onClick={() => setShowMobileActions(false)}
                    >
                      + Create article
                    </Link>
                    <button
                      className="community-actions-item"
                      onClick={handleSubscribe}
                      disabled={subscribeLoading}
                    >
                      {subscribeLoading ? 'Loading…' : isSubscribed ? 'Unsubscribe' : 'Subscribe'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          <button
            className="menu-open-btn right-toggle"
            onClick={onOpenRightSidebar}
            aria-label="Open user menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
        </div>
      </div>
      <div className="community-controls-bar">
        <div className="feed-tabs desktop-only">
          {SORT_OPTIONS.map(opt => {
            const disabled = opt.requiresAuth && !user;
            return (
              <button
                key={opt.value}
                className={`feed-tab ${activeSort === opt.value ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && handleSortChange(opt.value)}
                title={disabled ? 'Login to use Following' : undefined}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <select
          className="community-sort-dropdown mobile-only"
          value={activeSort}
          onChange={e => handleSortChange(e.target.value)}
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value} disabled={opt.requiresAuth && !user}>
              {opt.label}{opt.requiresAuth && !user ? ' (login required)' : ''}
            </option>
          ))}
        </select>
        {renderPinnedRow(false)}
      </div>
    </div>
  );

  if (error) {
    return (
      <>
        {stickyControls}
        <div className="community-view">
          <div className="error-message">{error}</div>
        </div>
      </>
    );
  }

  return (
    <>
      {stickyControls}
      {showAboutModal && (
        <div className="modal-overlay community-about-modal-overlay" onClick={() => setShowAboutModal(false)}>
          <div className="community-about-modal" onClick={e => e.stopPropagation()}>
            <div className="community-about-modal-header">
              <h3>{communityInfo?.title || `c/${community}`}</h3>
              <button className="community-about-modal-close modal-close" onClick={() => setShowAboutModal(false)} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            {(communityInfo?.about || communityInfo?.description) && (
              <>
                <h4 className="community-about-modal-rules-title">About</h4>
                <p className="community-about-modal-body">{communityInfo.about || communityInfo.description}</p>
              </>
            )}
            {communityInfo?.rules && (
              <div className="community-about-modal-rules">
                <h4 className="community-about-modal-rules-title">Community Rules</h4>
                <p className="community-about-modal-body">{communityInfo.rules}</p>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="community-view">
        <div className="community-posts">
          {postsLoading ? (
            <LoadingSpinner />
          ) : visiblePosts.length === 0 ? (
            <div className="no-posts">
              <p>No posts yet in this community. {user && 'Be the first to post!'}</p>
            </div>
          ) : (
            visiblePosts.map((post, index) => (
              <PostCard
                key={`${post.author}-${post.permlink}-${index}`}
                post={post}
                showActions={true}
                communitySlug={community}
                communityDisplayName={communityInfo?.title}
                inactivePinned={!showCommunityPinned}
              />
            ))
          )}
          
          {postsLoading && posts.length > 0 && (
            <div style={{ padding: '20px 0' }}><LoadingSpinner /></div>
          )}

          {feedPaused && (
            <div className="feed-pause-notice" style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: '8px', margin: '1rem 0' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                You've seen all the posts from the last 7 days.
              </p>
              <button className="primary-btn" onClick={handleLoadOlderPosts}>
                Load older posts
              </button>
            </div>
          )}

          {hasMore && !feedPaused && (
            <div className="load-more-container" style={{ textAlign: 'center', margin: '2rem 0' }}>
              <button className="secondary-btn" onClick={() => loadCommunity(true)} disabled={postsLoading}>
                {postsLoading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>

        {showCreatePost && (
          <CreatePostModal onClose={() => setShowCreatePost(false)} />
        )}
      </div>
    </>
  );
};

export default FeedView;
